import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Card, Typography, message, Alert, Image, Space } from 'antd';
import { UserOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { authApi } from '../../services/api';
import { usePermissions } from '../../context/PermissionContext';

const { Title, Text } = Typography;

interface LoginForm {
  username: string;
  password: string;
  captcha: string;
}

interface CaptchaData {
  id: string;
  image: string;
  expire: number;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshPermissions } = usePermissions();
  
  // 使用ref存储错误信息，防止组件重新挂载时丢失
  const errorMessageRef = useRef<string>('');
  const lastErrorTimeRef = useRef<number>(0);
  


  // 错误信息管理函数
  const setErrorMessageWithPersistence = (msg: string) => {
    const now = Date.now();
    
    // 同时保存到ref和localStorage，防止组件重新挂载时丢失
    errorMessageRef.current = msg;
    lastErrorTimeRef.current = now;
    localStorage.setItem('login_error_message', msg);
    localStorage.setItem('login_error_time', now.toString());
    
    setErrorMessage(msg);
  };

  const clearErrorMessageWithPersistence = () => {
    // 清除ref和localStorage中的错误信息
    errorMessageRef.current = '';
    lastErrorTimeRef.current = 0;
    localStorage.removeItem('login_error_message');
    localStorage.removeItem('login_error_time');
    
    setErrorMessage('');
  };

  // 检查是否有注册成功的消息
  useEffect(() => {
    const state = location.state as any;
    if (state?.message) {
      const logMsg = `🔄 [${new Date().toISOString()}] 检测到注册成功消息，将清除location state`;
      console.log(logMsg);
      // 保存到localStorage
      const logs = JSON.parse(localStorage.getItem('login_debug_logs') || '[]');
      logs.push(logMsg);
      localStorage.setItem('login_debug_logs', JSON.stringify(logs.slice(-20)));
      
      message.success(state.message);
      if (state.username) {
        form.setFieldsValue({ username: state.username });
      }
      // 清除location state
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, form]);

  // 获取验证码
  const fetchCaptcha = async (silent = false) => {
    try {
      const response = await authApi.getCaptcha();
      setCaptcha(response.data);
    } catch (error) {
      console.error('获取验证码失败:', error);
      // 只有在非静默模式下才设置错误信息
      if (!silent) {
        setErrorMessageWithPersistence('获取验证码失败，请稍后重试');
      }
    }
  };

  // 页面加载时获取验证码和恢复错误信息
  useEffect(() => {
    // 恢复错误信息（如果组件重新挂载）
    const savedError = localStorage.getItem('login_error_message');
    const savedTime = localStorage.getItem('login_error_time');
    
    if (savedError && savedTime) {
      const errorTime = parseInt(savedTime);
      const now = Date.now();
      
      // 如果错误信息是在最近10秒内设置的，则恢复它
      if (now - errorTime < 10000) {
        errorMessageRef.current = savedError;
        lastErrorTimeRef.current = errorTime;
        setErrorMessage(savedError);
      } else {
        // 错误信息太旧，清除它
        localStorage.removeItem('login_error_message');
        localStorage.removeItem('login_error_time');
      }
    }
    
    fetchCaptcha();
  }, []);

  const handleLogin = async (values: LoginForm) => {
    try {
      setLoading(true);
      
      // 用户名密码登录
      if (!captcha) {
        setErrorMessageWithPersistence('请先获取验证码');
        return;
      }
      
      // 开始发送登录请求时清除之前的错误信息
      clearErrorMessageWithPersistence();
      
      const response = await authApi.login({
        username: values.username,
        password: values.password,
        captcha_id: captcha.id,
        captcha_value: values.captcha,
      });
      
      // 登录成功，清除错误信息
      clearErrorMessageWithPersistence();
      
      // 存储tokens
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      localStorage.setItem('user_info', JSON.stringify(response.data.user));
      
      message.loading('正在获取用户权限...', 0.5);
      await refreshPermissions();
      
      message.success(`欢迎回来，${response.data.user.name}！`);
      navigate('/admin'); // 使用智能重定向，自动跳转到第一个有权限的页面
    } catch (error: any) {
      console.error('Login error:', error);
      
      // 获取具体的错误信息并设置到状态中
      let errorMsg = '';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMsg = '用户名、密码或验证码错误，请检查后重试';
      } else if (error.response?.status === 403) {
        errorMsg = '权限不足，需要管理员权限才能访问';
      } else if (error.response?.status === 400) {
        errorMsg = error.response?.data?.error || '请求参数错误，请检查输入内容';
      } else if (error.response?.status === 500) {
        errorMsg = '服务器内部错误，请稍后重试';
      } else if (error.response?.status === 404) {
        errorMsg = '请求的接口不存在，请检查服务器配置';
      } else {
        errorMsg = '网络连接失败，请检查网络后重试';
      }
      
      // 设置错误信息到状态中
      setErrorMessageWithPersistence(errorMsg);
      
      // 重新获取验证码，使用静默模式不覆盖当前的错误信息
      fetchCaptcha(true);
      
      // 开发环境下打印详细错误信息
      if (process.env.NODE_ENV === 'development') {
        console.group('登录错误详情:');
        console.log('状态码:', error.response?.status);
        console.log('错误信息:', error.response?.data);
        console.log('完整错误:', error);
        console.groupEnd();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 450, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2}>APILink 管理后台</Title>
          <Text type="secondary">管理员登录</Text>
        </div>

        <Alert
          message="管理员登录"
          description="使用管理员用户名和密码登录，需要输入验证码。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
        
        <Form
          form={form}
          name="adminLogin"
          onFinish={handleLogin}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="请输入用户名"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入密码"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item
            name="captcha"
            rules={[
              { required: true, message: '请输入验证码' }
            ]}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="请输入验证码"
                maxLength={4}
                style={{ flex: 1 }}
              />
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                border: '1px solid #d9d9d9',
                borderLeft: 'none',
                borderRadius: '0 6px 6px 0',
                padding: '4px 8px',
                backgroundColor: '#fafafa',
                cursor: 'pointer'
              }} onClick={() => fetchCaptcha()}>
                {captcha ? (
                  <Image
                    src={captcha.image}
                    alt="验证码"
                    width={80}
                    height={32}
                    preview={false}
                    style={{ display: 'block' }}
                  />
                ) : (
                  <div style={{ width: 80, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ReloadOutlined />
                  </div>
                )}
              </div>
            </Space.Compact>
          </Form.Item>

          {/* 错误信息显示区域 */}
          {errorMessage && (
            <div 
              style={{
                marginBottom: 16,
                padding: '12px 16px',
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{ color: '#ff4d4f', fontSize: '14px' }}>⚠️</span>
              <span style={{ color: '#ff4d4f', fontSize: '14px', flex: 1 }}>{errorMessage}</span>
              <Button 
                type="text" 
                size="small" 
                htmlType="button"
                onClick={() => clearErrorMessageWithPersistence()}
                style={{ color: '#ff4d4f', padding: '0 4px', minWidth: 'auto' }}
              >
                ✕
              </Button>
            </div>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: '100%' }}
            >
              登录
            </Button>
          </Form.Item>
        </Form>
        
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Space>
            <Text type="secondary">还没有账户？</Text>
            <Link to="/register">立即注册</Link>
          </Space>
        </div>
        

        
        <div style={{ textAlign: 'center', marginTop: 16, color: '#666', fontSize: '12px' }}>
          <p>© 2024 APILink. All rights reserved.</p>
        </div>
      </Card>
    </div>
  );
};

export default Login; 